import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, IconButton, LinearProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DeleteOutline, Download, Visibility } from '@mui/icons-material';
import CapturarFoto, { FotoLocal } from '../../../form/CapturarFoto';
import VisualizadorArquivo from './VisualizadorArquivo';
import * as API from '../../../../api/pocos';
import { mensagemErro } from '../modules/valores';

export default function Arquivos({ tipo, registro, podeAnexar = false }) {
    const [rows, setRows] = useState([]), [loading, setLoading] = useState(true), [erro, setErro] = useState('');
    const [files, setFiles] = useState([]), [categoria, setCategoria] = useState(tipo === 'outorgas' ? 'OUTORGA' : 'FOTO_LEITURA');
    const [observacao, setObservacao] = useState(''), [busy, setBusy] = useState(false), [revision, setRevision] = useState(0);
    const [preview, setPreview] = useState(null), [sucesso, setSucesso] = useState('');
    const lock = useRef(false), mounted = useRef(true), urls = useRef(new Set()), input = useRef(null);
    const targetKey = tipo === 'outorgas' ? 'outorgaId' : 'pocoMedicaoId';
    useEffect(() => {
        mounted.current = true;
        const pendingUrls = urls.current;
        return () => { mounted.current = false; pendingUrls.forEach(url => URL.revokeObjectURL(url)); pendingUrls.clear(); };
    }, []);
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true); setErro('');
        API.consultar('arquivos/listar', { [targetKey]: registro.id }, { signal: controller.signal })
            .then(r => { if (!controller.signal.aborted) setRows(r.dados); })
            .catch(e => { if (!controller.signal.aborted) setErro(mensagemErro(e)); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [targetKey, registro.id, revision]);
    function closePreview() {
        if (preview) { URL.revokeObjectURL(preview.url); urls.current.delete(preview.url); }
        setPreview(null);
    }
    async function fileAction(row, download) {
        if (lock.current) return;
        lock.current = true; setBusy(true); setErro('');
        try {
            const blob = await API.obterArquivo(row.uuid, download);
            if (!mounted.current) return;
            const url = URL.createObjectURL(blob); urls.current.add(url);
            if (download) {
                const a = document.createElement('a'); a.href = url; a.download = row.nomeOriginal || 'arquivo';
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => { URL.revokeObjectURL(url); urls.current.delete(url); }, 60000);
            } else setPreview({ url, row });
        } catch (e) {
            let message = mensagemErro(e);
            if (e.response?.data instanceof Blob) {
                try { message = JSON.parse(await e.response.data.text()).message || message; } catch { /* erro não JSON */ }
            }
            if (mounted.current) setErro(message);
        } finally { lock.current = false; if (mounted.current) setBusy(false); }
    }
    function selecionar(novos) {
        setSucesso(''); setErro('');
        const propostos = [...files, ...novos];
        if (propostos.length > 5 || propostos.some(f => f.size > 10 * 1024 * 1024)) {
            setErro('Selecione até 5 arquivos de no máximo 10 MB cada. A seleção anterior foi mantida.'); return;
        }
        const permitidos = categoria.startsWith('FOTO_') ? ['image/jpeg', 'image/png'] : ['image/jpeg', 'image/png', 'application/pdf'];
        if (propostos.some(f => !permitidos.includes(f.type))) {
            setErro('Formato não aceito. Use JPEG ou PNG para fotos; PDF também é permitido em documentos. A seleção anterior foi mantida.'); return;
        }
        setFiles(propostos);
    }
    async function upload() {
        if (lock.current || !files.length || !podeAnexar) return;
        lock.current = true; setBusy(true); setErro(''); setSucesso('');
        try {
            if (files.length > 5 || files.some(f => f.size > 10 * 1024 * 1024)) throw new Error('Envie até 5 arquivos, com no máximo 10 MB cada.');
            if (categoria.startsWith('FOTO_') && files.some(f => !['image/jpeg', 'image/png'].includes(f.type))) throw new Error('Categorias de foto aceitam somente JPEG ou PNG.');
            await API.anexar({ [targetKey]: registro.id }, categoria, files, observacao);
            if (!mounted.current) return;
            setFiles([]); setObservacao(''); if (input.current) input.current.value = '';
            setSucesso('Arquivos anexados.'); setRevision(n => n + 1);
        } catch (e) { if (mounted.current) setErro(`${mensagemErro(e)} O registro permanece salvo; confira a lista antes de reenviar.`); }
        finally { lock.current = false; if (mounted.current) setBusy(false); }
    }
    const categories = tipo === 'outorgas' ? ['OUTORGA', 'OUTRO'] : ['FOTO_LEITURA', 'FOTO_LACRE', 'FOTO_MEDIDOR', 'OUTRO'];
    return <Box sx={{ p: 1, minWidth: 0 }}>
        <Typography variant="subtitle1">Arquivos e evidências</Typography>
        {(loading || busy) && <LinearProgress sx={{ my: 1 }} />}
        {erro && <Alert severity="error">{erro}</Alert>}
        {sucesso && <Alert severity="success">{sucesso}</Alert>}
        {!loading && !erro && !rows.length && <Alert severity="warning">Nenhum arquivo anexado a este registro.</Alert>}
        {rows.map(row => <Box key={row.uuid} sx={{ py: 1.5, minWidth: 0, borderBottom: 1, borderColor: 'divider' }}>
            <Typography sx={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}>{row.nomeOriginal || 'Arquivo anexado'}</Typography>
            <Typography variant="caption">{row.categoria?.replaceAll('_', ' ')}</Typography>
            <Stack direction="row" sx={{ mt: 1, gap: 1, flexWrap: 'wrap' }}>
                <Button type="button" variant="outlined" startIcon={<Visibility />}
                    title="Visualizar este arquivo; fotos abrem no visualizador com zoom"
                    aria-label={`Visualizar ${row.nomeOriginal || 'arquivo'}`} disabled={busy}
                    onClick={() => fileAction(row, false)}>Visualizar</Button>
                <Button type="button" variant="outlined" startIcon={<Download />}
                    title="Baixar o arquivo original"
                    aria-label={`Baixar ${row.nomeOriginal || 'arquivo'}`} disabled={busy}
                    onClick={() => fileAction(row, true)}>Baixar</Button>
            </Stack>
        </Box>)}
        <Button disabled={loading || busy} onClick={() => setRevision(n => n + 1)}>Atualizar arquivos</Button>
        {podeAnexar && <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField select size="small" label="Categoria" title="Finalidade do anexo: documento de outorga, foto da leitura, do lacre, do medidor ou outro comprovante." value={categoria} disabled={busy} onChange={e => { setCategoria(e.target.value); setErro(''); }}>
                {categories.map(c => <MenuItem key={c} value={c}>{c.replaceAll('_', ' ')}</MenuItem>)}
            </TextField>
            <Stack direction="row" flexWrap="wrap" spacing={1}>
                <Button component="label" disabled={busy} title="Adicionar arquivos à seleção, até 5 por envio">
                    Selecionar arquivos
                    <input hidden ref={input} type="file" multiple
                        accept={categoria.startsWith('FOTO_') ? 'image/jpeg,image/png' : 'image/jpeg,image/png,application/pdf'}
                        disabled={busy} onChange={e => { selecionar(Array.from(e.target.files || [])); e.target.value = ''; }} />
                </Button>
                {tipo === 'medicoes' && <CapturarFoto nome="medidor" disabled={busy} onCapture={file => selecionar([file])} />}
            </Stack>
            {!!files.length && <Typography variant="body2">Aguardando envio: {files.length} arquivo(s). Confira a nitidez da leitura e do lacre.</Typography>}
            <Stack direction="row" flexWrap="wrap" gap={1}>
                {files.map((file, i) => <Box key={i} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, width: 180 }}>
                    {file.type.startsWith('image/') && <FotoLocal arquivo={file} sx={{ width: '100%', height: 120, objectFit: 'contain' }} />}
                    <Typography variant="caption" sx={{ display: 'block', overflowWrap: 'anywhere' }}>{file.name}</Typography>
                    <IconButton title="Remover somente da seleção; este arquivo ainda não foi enviado" aria-label="Remover da seleção"
                        disabled={busy} onClick={() => setFiles(prev => prev.filter((_, n) => n !== i))}><DeleteOutline /></IconButton>
                </Box>)}
            </Stack>
            <TextField size="small" label="Observação dos anexos" title="Descrição ou ressalva que será associada aos arquivos enviados neste lote." value={observacao} disabled={busy} onChange={e => setObservacao(e.target.value)} />
            <Button variant="contained" title="Gravar os arquivos selecionados nesta medição ou outorga" disabled={busy || !files.length} onClick={upload}>Enviar arquivos</Button>
            <Typography variant="caption">JPEG, PNG ou PDF, até 5 arquivos de 10 MB. Fotos da leitura devem mostrar a contagem e o lacre de forma legível.</Typography>
        </Stack>}
        {preview && <VisualizadorArquivo key={preview.url} preview={preview} onClose={closePreview} />}
    </Box>;
}
